import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const PRESET_COLORS = [
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Teal', hex: '#14B8A6' },
]

interface AddClassificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  onClassificationCreated: () => void
}

export function AddClassificationDialog({
  open,
  onOpenChange,
  groupId,
  onClassificationCreated,
}: AddClassificationDialogProps) {
  const [displayName, setDisplayName] = useState('')
  const [color, setColor] = useState(PRESET_COLORS[0].hex)
  const [alertEnabled, setAlertEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()

  const derivedName = displayName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError(t('addClassification.displayNameRequired'))
      return
    }

    if (!derivedName) {
      setError(t('addClassification.mustContainAlphanumeric'))
      return
    }

    setSaving(true)
    setError(null)

    try {
      const createClassificationFn = apiCallable('createClassification')
      await createClassificationFn({
        groupId,
        name: derivedName,
        displayName: displayName.trim(),
        alertEnabled,
        color,
      })

      handleClose()
      onClassificationCreated()
    } catch (err: any) {
      console.error('Failed to create classification:', err)
      if (err.code === 'already-exists') {
        setError(t('addClassification.alreadyExists'))
      } else if (err.code === 'permission-denied') {
        setError(t('addClassification.noPermission'))
      } else {
        setError(t('addClassification.failedToCreate'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setDisplayName('')
    setColor(PRESET_COLORS[0].hex)
    setAlertEnabled(false)
    setError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('addClassification.title')}</DialogTitle>
          <DialogDescription>
            {t('addClassification.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">{t('addClassification.displayName')}</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('addClassification.displayNamePlaceholder')}
              autoFocus
            />
            {derivedName && (
              <p className="text-xs text-muted-foreground">
                {t('addClassification.internalName', { name: derivedName })}
              </p>
            )}
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>{t('addClassification.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.hex}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === preset.hex
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:border-muted-foreground/50'
                  }`}
                  style={{ backgroundColor: preset.hex }}
                  onClick={() => setColor(preset.hex)}
                  title={preset.name}
                />
              ))}
            </div>
          </div>

          {/* Alert Enabled */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-normal">{t('addClassification.enableAlerts')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('addClassification.alertsHint')}
              </p>
            </div>
            <Switch
              checked={alertEnabled}
              onCheckedChange={setAlertEnabled}
            />
          </div>

          {/* Preview */}
          {displayName.trim() && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="font-medium text-sm">{displayName.trim()}</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {alertEnabled ? t('classifications.alertsOn') : t('classifications.alertsOff')}
              </span>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving} className="hidden sm:inline-flex">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !displayName.trim()}>
            {saving ? t('common.creating') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
